/**
 * PortalHeader — slim header for portal-only users (agency owners).
 *
 * The full Header is the rep cockpit: clock in/out, presence status,
 * softphone, voicemail, team drawer — none of which applies to an
 * external agency owner, and all of which calls rep APIs an agency
 * owner has no permissions for. This variant keeps just identity,
 * accessibility toggles, and logout.
 */

import { useState, useEffect, useRef } from 'react';
import { UserCircle, ChevronDown, LogOut, KeyRound } from 'lucide-react';
import type { User } from '../../providers/AuthProvider';
import { useAuth } from '../../providers/AuthProvider';
import { FontSizeToggle } from './FontSizeToggle';
import { ThemeToggle } from './ThemeToggle';
import { UserSwitcher } from './UserSwitcher';

interface PortalHeaderProps {
  user: User | null;
}

export function PortalHeader({ user }: PortalHeaderProps) {
  const { logout } = useAuth();

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <header className="h-16 border-b border-border/60 flex items-center justify-between bg-card px-6 z-50 shadow-[0_2px_8px_rgba(0,0,0,0.02)] flex-shrink-0 relative">
      <div className="text-[0.875rem] font-semibold text-foreground">Agency Owner Portal</div>

      <div className="flex items-center gap-4">
        {/* Platform-ops escape hatch — renders null for everyone else, so a
            real agency owner never sees it. Keeps the switcher reachable
            when impersonation collapses the shell to the portal. */}
        <UserSwitcher />

        <FontSizeToggle />
        <ThemeToggle />

        <div className="h-5 w-px bg-border/60" />

        {/* User menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className={`flex items-center gap-2 px-2.5 py-1 transition-colors rounded-full border cursor-pointer ${userMenuOpen ? 'bg-secondary border-border/60' : 'hover:bg-secondary border-transparent'}`}
          >
            <UserCircle className="w-6 h-6 text-slate-400 stroke-[1.5]" />
            <div className="text-left hidden md:block">
              <p className="text-[0.75rem] font-medium leading-none text-foreground">
                {user?.name || 'User'}
              </p>
              <p className="text-[0.625rem] text-muted-foreground leading-none mt-1">
                Agency Owner
              </p>
            </div>
            <ChevronDown
              className={`w-3 h-3 text-muted-foreground transition-transform hidden md:block ${userMenuOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {userMenuOpen && (
            <div className="absolute top-full right-0 mt-1.5 w-[200px] bg-card border border-border/80 rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] z-50 overflow-hidden">
              <div className="px-4 py-3 border-b border-border/40">
                <p className="text-[0.8125rem] font-semibold text-foreground truncate">
                  {user?.name || 'User'}
                </p>
                <p className="text-[0.6875rem] text-muted-foreground truncate mt-0.5">
                  {user?.email || ''}
                </p>
              </div>

              <div className="py-1">
                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    void logout();
                  }}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-[0.75rem] text-destructive hover:bg-destructive/5 transition-colors cursor-pointer"
                >
                  <LogOut className="w-3.5 h-3.5 stroke-[1.5]" />
                  Log out
                </button>

                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    void logout({ forgetLoginMethod: true });
                  }}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-[0.75rem] text-foreground hover:bg-secondary transition-colors cursor-pointer"
                >
                  <KeyRound className="w-3.5 h-3.5 text-muted-foreground stroke-[1.5]" />
                  Change sign-in method
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
